import { useState } from "react";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Link as RouterLink } from "react-router-dom";
import MDButton from "components/MDButton";
import {
  CONTAS_FIN_ACCENT,
  saveButtonSx,
} from "../../components/contas-financeiras.theme";
import type { ContaFinanceiraGerencialApi } from "../types/contas-financeiras.types";
import { isContaGatewayEfi } from "../types/contas-financeiras.types";
import { formatarMoedaBRL } from "../helpers/financeiro-formatters";
import {
  saldoDisponivelTransferencia,
  temDivergenciaSaldoEfi,
} from "../helpers/contas-financeiras-saldo-efi";

function iniciais(nome: string): string {
  const parts = nome.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "CF";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export type ContaFinanceiraCardDestaque = "efi" | "repasse" | "default";

type Props = {
  conta: ContaFinanceiraGerencialApi;
  destaque?: ContaFinanceiraCardDestaque;
  onEditar: (conta: ContaFinanceiraGerencialApi) => void;
  onMarcarPadrao: (conta: ContaFinanceiraGerencialApi) => void;
  onAlternarAtivo: (conta: ContaFinanceiraGerencialApi) => void;
  onTransferir?: (conta: ContaFinanceiraGerencialApi) => void;
  onRepasseCaixa?: (conta: ContaFinanceiraGerencialApi) => void;
  onConciliarSaldo?: (conta: ContaFinanceiraGerencialApi) => void;
  onExcluir?: (conta: ContaFinanceiraGerencialApi) => void;
  podeVerSaldoEfi?: boolean;
  efiSandbox?: boolean;
  onAtualizarSaldoEfi?: () => void;
  onConciliarSaldoEfi?: () => void;
  atualizandoSaldoEfi?: boolean;
  conciliandoSaldoEfi?: boolean;
};

const alertCompactSx = {
  mt: 0.5,
  py: 0,
  px: 1,
  alignItems: "flex-start",
  "& .MuiAlert-message": { fontSize: "0.7rem", lineHeight: 1.35, py: 0.4 },
  "& .MuiAlert-icon": { fontSize: "0.95rem", mr: 0.5, py: 0.4, mt: 0.1 },
};

const cardRootSx = {
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column" as const,
  borderRadius: 2,
  border: 1,
};

const metricsGridSx = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 1,
  minHeight: 76,
  alignContent: "start",
};

function MetricaMes({
  label,
  valor,
  color,
}: {
  label: string;
  valor: number;
  color?: "success.main" | "error.main" | "warning.main" | "text.primary";
}) {
  return (
    <Box flex={1} minWidth={0}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} color={color ?? "text.primary"}>
        {valor > 0 ? formatarMoedaBRL(valor) : "—"}
      </Typography>
    </Box>
  );
}

export default function ContaFinanceiraCard({
  conta,
  destaque = "default",
  onEditar,
  onMarcarPadrao,
  onAlternarAtivo,
  onTransferir,
  onRepasseCaixa,
  onConciliarSaldo,
  onExcluir,
  podeVerSaldoEfi,
  efiSandbox,
  onAtualizarSaldoEfi,
  onConciliarSaldoEfi,
  atualizandoSaldoEfi,
  conciliandoSaldoEfi,
}: Props) {
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const [extratoExpandido, setExtratoExpandido] = useState(false);

  const opaco = !conta.ativo;
  const gatewayEfi = isContaGatewayEfi(conta);
  const exibirDetalheSaldoEfi = gatewayEfi && conta.ativo;
  const divergencia = temDivergenciaSaldoEfi(conta);
  const temBloqueio = (conta.saldoEfiBloqueio ?? 0) > 0.009;
  const saldoRepasseDisponivel =
    destaque === "efi" ? saldoDisponivelTransferencia(conta) : conta.saldoAtual;
  const repasseBloqueado = saldoRepasseDisponivel <= 0.01;

  const saldoPrincipal =
    destaque === "efi" && conta.saldoEfiDisponivel != null
      ? conta.saldoEfiDisponivel
      : conta.saldoAtual;

  const labelSaldoPrincipal =
    destaque === "efi"
      ? "Saldo disponível na Efí"
      : destaque === "repasse"
        ? "Saldo atual"
        : gatewayEfi && conta.saldoEfiDisponivel != null
          ? "Saldo no extrato"
          : "Saldo atual";

  const linkExtrato = `/loja/financeiro/extrato-financeiro?conta=${encodeURIComponent(conta.id)}`;
  const destacado = destaque === "efi" || destaque === "repasse";
  const mostrarRodape = conta.ativo && (destacado || destaque === "default");

  return (
    <Card
      elevation={0}
      sx={{
        ...cardRootSx,
        borderColor:
          destaque === "efi" || destaque === "repasse" || conta.ehPadrao
            ? CONTAS_FIN_ACCENT
            : "grey.200",
        opacity: opaco ? 0.72 : 1,
        bgcolor:
          destaque === "efi" || destaque === "repasse" || conta.ehPadrao
            ? "rgba(249, 96, 48, 0.04)"
            : "background.paper",
      }}
    >
      <CardContent
        sx={{
          p: destacado ? 2 : 2.5,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          "&:last-child": { pb: destacado ? 2 : 2.5 },
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ flexShrink: 0 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: "rgba(249, 96, 48, 0.12)",
              color: CONTAS_FIN_ACCENT,
              fontWeight: 700,
              fontSize: "0.875rem",
            }}
          >
            {iniciais(conta.nome)}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                noWrap
                title={conta.nome}
              >
                {conta.nome}
              </Typography>
              {conta.ehPadrao ? (
                <Icon sx={{ fontSize: 18, color: "warning.main" }}>star</Icon>
              ) : null}
            </Stack>
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }} flexWrap="wrap">
              <Chip label={conta.tipoLabel} size="small" variant="outlined" />
              {destaque === "efi" ? (
                <Chip label="Recebimentos" size="small" sx={{ bgcolor: "rgba(249, 96, 48, 0.12)", color: CONTAS_FIN_ACCENT }} />
              ) : null}
              {destaque === "repasse" ? (
                <Chip label="Repasse" size="small" sx={{ bgcolor: "rgba(249, 96, 48, 0.12)", color: CONTAS_FIN_ACCENT }} />
              ) : null}
              {!conta.ativo ? (
                <Chip label="Inativa" size="small" color="default" />
              ) : null}
            </Stack>
          </Box>
          <IconButton
            size="small"
            aria-label="Ações da conta"
            onClick={(e) => setMenuEl(e.currentTarget)}
          >
            <Icon fontSize="small">more_vert</Icon>
          </IconButton>
          <Menu
            anchorEl={menuEl}
            open={Boolean(menuEl)}
            onClose={() => setMenuEl(null)}
          >
            <MenuItem
              onClick={() => {
                setMenuEl(null);
                onEditar(conta);
              }}
            >
              Editar
            </MenuItem>
            {!conta.ehPadrao && conta.ativo ? (
              <MenuItem
                onClick={() => {
                  setMenuEl(null);
                  void onMarcarPadrao(conta);
                }}
              >
                Marcar como padrão
              </MenuItem>
            ) : null}
            {conta.ativo && onTransferir ? (
              <MenuItem
                onClick={() => {
                  setMenuEl(null);
                  onTransferir(conta);
                }}
              >
                Transferência entre contas
              </MenuItem>
            ) : null}
            {conta.ativo && (!gatewayEfi || efiSandbox) && onConciliarSaldo ? (
              <MenuItem
                onClick={() => {
                  setMenuEl(null);
                  onConciliarSaldo(conta);
                }}
              >
                {gatewayEfi && efiSandbox
                  ? "Ajustar extrato interno"
                  : "Ajustar saldo (juros, tarifas)"}
              </MenuItem>
            ) : null}
            <MenuItem
              onClick={() => {
                setMenuEl(null);
                void onAlternarAtivo(conta);
              }}
            >
              {conta.ativo ? "Desativar" : "Reativar"}
            </MenuItem>
            {conta.podeExcluir && onExcluir ? (
              <MenuItem
                sx={{ color: "error.main" }}
                onClick={() => {
                  setMenuEl(null);
                  onExcluir(conta);
                }}
              >
                Excluir
              </MenuItem>
            ) : null}
          </Menu>
        </Stack>

        <Box sx={{ mt: 1.5, flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {labelSaldoPrincipal}
            </Typography>
            {destaque === "efi" && onAtualizarSaldoEfi && podeVerSaldoEfi ? (
              <Tooltip title="Atualizar saldo na Efí">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Atualizar saldo Efí"
                    onClick={() => void onAtualizarSaldoEfi()}
                    disabled={atualizandoSaldoEfi}
                  >
                    <RefreshIcon
                      fontSize="small"
                      sx={{
                        animation: atualizandoSaldoEfi
                          ? "efi-spin 0.8s linear infinite"
                          : "none",
                        "@keyframes efi-spin": {
                          from: { transform: "rotate(0deg)" },
                          to: { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Stack>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              mt: 0.25,
              color: destaque === "efi" ? CONTAS_FIN_ACCENT : undefined,
            }}
          >
            {formatarMoedaBRL(saldoPrincipal)}
          </Typography>

          {destaque === "efi" && exibirDetalheSaldoEfi ? (
            <Box
              sx={{
                mt: 0.75,
                maxHeight: 96,
                overflowY: "auto",
                pr: 0.25,
              }}
            >
              {conta.saldoEfiErro ? (
                <Alert severity="warning" sx={alertCompactSx}>
                  {conta.saldoEfiErro}
                </Alert>
              ) : null}
              {efiSandbox ? (
                <Alert severity="info" sx={alertCompactSx}>
                  Homologação: saldo Efí pode não refletir webhooks de teste.
                </Alert>
              ) : null}
              {divergencia ? (
                <Alert severity="warning" sx={alertCompactSx}>
                  {efiSandbox ? (
                    <>
                      Extrato interno ({formatarMoedaBRL(conta.saldoAtual)}) difere da Efí (
                      {formatarMoedaBRL(conta.saldoEfiDisponivel ?? 0)}). Em homologação o
                      repasse fica bloqueado quando a Efí está zerada — use «Conciliar extrato
                      Efí» para alinhar.
                    </>
                  ) : (
                    <>
                      Extrato interno ({formatarMoedaBRL(conta.saldoAtual)}) difere da Efí (
                      {formatarMoedaBRL(conta.saldoEfiDisponivel ?? 0)}). Use repasse ou
                      concilie.
                    </>
                  )}
                </Alert>
              ) : null}
              {temBloqueio ? (
                <Typography
                  variant="caption"
                  color="warning.main"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  Bloqueios (MED/judicial):{" "}
                  {formatarMoedaBRL(conta.saldoEfiBloqueio ?? 0)}
                </Typography>
              ) : null}
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={() => setExtratoExpandido((v) => !v)}
                sx={{ mt: 0.5, display: "inline-block" }}
              >
                {extratoExpandido ? "Ocultar" : "Ver"} saldo no extrato interno
              </Link>
              <Collapse in={extratoExpandido}>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Extrato: {formatarMoedaBRL(conta.saldoAtual)}
                </Typography>
              </Collapse>
            </Box>
          ) : null}
        </Box>

        <Box sx={{ flex: 1, minHeight: 8 }} />

        <Divider sx={{ my: 1.5, flexShrink: 0 }} />

        <Box sx={{ ...metricsGridSx, flexShrink: 0 }}>
          {destaque === "efi" ? (
            <>
              <MetricaMes
                label="Recebido no mês (bruto)"
                valor={conta.receitasMes}
                color="success.main"
              />
              <MetricaMes
                label="Taxas Efí no mês"
                valor={conta.taxasMes ?? 0}
                color="warning.main"
              />
              <MetricaMes label="Líquido no mês" valor={conta.liquidoMes ?? 0} />
              <MetricaMes
                label="Repasses (saídas)"
                valor={conta.despesasMes}
                color="error.main"
              />
            </>
          ) : destaque === "repasse" ? (
            <>
              <MetricaMes
                label="Recebido no mês (repasses)"
                valor={conta.receitasMes}
                color="success.main"
              />
              <MetricaMes
                label="Saídas no mês"
                valor={conta.despesasMes}
                color="error.main"
              />
              <MetricaMes label="Líquido no mês" valor={conta.liquidoMes ?? 0} />
              <Box />
            </>
          ) : (
            <>
              <MetricaMes
                label="Receitas do mês"
                valor={conta.receitasMes}
                color="success.main"
              />
              <MetricaMes
                label="Despesas do mês"
                valor={conta.despesasMes}
                color="error.main"
              />
              <MetricaMes label="Líquido no mês" valor={conta.liquidoMes ?? 0} />
              <Box />
            </>
          )}
        </Box>

        {mostrarRodape ? (
          <Stack
            spacing={0.75}
            sx={{ mt: "auto", pt: 1.5, flexShrink: 0, minHeight: destacado ? 92 : 44 }}
          >
            {destaque === "efi" && onRepasseCaixa ? (
              <Tooltip
                title={
                  repasseBloqueado
                    ? `Repasse indisponível: saldo na Efí é ${formatarMoedaBRL(conta.saldoEfiDisponivel ?? 0)}. Concilie o extrato ou aguarde saldo na API.`
                    : ""
                }
              >
                <span>
                  <MDButton
                    variant="gradient"
                    color="dark"
                    size="small"
                    fullWidth
                    disabled={repasseBloqueado}
                    onClick={() => onRepasseCaixa(conta)}
                    sx={{ ...saveButtonSx, py: 0.75 }}
                  >
                    Registrar repasse
                  </MDButton>
                </span>
              </Tooltip>
            ) : null}

            <Stack direction="row" spacing={0.75}>
              {destaque === "efi" &&
              divergencia &&
              onConciliarSaldoEfi &&
              podeVerSaldoEfi ? (
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  fullWidth
                  disabled={conciliandoSaldoEfi}
                  onClick={() => void onConciliarSaldoEfi()}
                  sx={{ borderColor: CONTAS_FIN_ACCENT, color: CONTAS_FIN_ACCENT }}
                >
                  {conciliandoSaldoEfi ? "Conciliando…" : "Conciliar extrato Efí"}
                </MDButton>
              ) : null}

              {destaque === "repasse" && onConciliarSaldo ? (
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  fullWidth
                  onClick={() => onConciliarSaldo(conta)}
                  sx={{ borderColor: CONTAS_FIN_ACCENT, color: CONTAS_FIN_ACCENT, py: 0.75 }}
                >
                  Ajustar saldo
                </MDButton>
              ) : null}

              <MDButton
                component={RouterLink}
                to={linkExtrato}
                variant="outlined"
                color="dark"
                size="small"
                fullWidth
                sx={{ py: 0.75 }}
              >
                Ver extrato
              </MDButton>
            </Stack>
          </Stack>
        ) : null}
      </CardContent>
    </Card>
  );
}
